import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { QuotaIndicator } from "@/components/QuotaIndicator";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetTrigger,
} from "@/components/ui/sheet";
import { LayoutDashboard, FolderKanban, Tag, User, LogOut, Menu, Zap } from "lucide-react";

const NavItems = ({ onNavigate }) => {
  const { t } = useApp();
  const items = [
    { to: "/app/dashboard", icon: LayoutDashboard, label: t("navDashboard"), testid: "nav-dashboard" },
    { to: "/app/portfolios", icon: FolderKanban, label: t("navPortfolios"), testid: "nav-portfolios" },
    { to: "/app/pricing", icon: Tag, label: t("navPricing"), testid: "nav-pricing" },
    { to: "/app/account", icon: User, label: t("navAccount"), testid: "nav-account" },
  ];
  return (
    <nav className="flex flex-col gap-1">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          onClick={onNavigate}
          data-testid={it.testid}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`
          }
        >
          <it.icon className="h-4 w-4" />
          {it.label}
        </NavLink>
      ))}
    </nav>
  );
};

const Brand = () => (
  <Link to="/app/dashboard" className="flex items-center gap-2">
    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
      <Zap className="h-4 w-4" />
    </span>
    <span className="font-heading text-lg font-semibold tracking-tight">DomainPitch<span className="text-primary">AI</span></span>
  </Link>
);

export const AppShell = ({ children }) => {
  const { user, logout, t } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = (user?.email || "?").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar - desktop */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r bg-card px-4 py-5 lg:flex">
        <Brand />
        <div className="mt-8 flex-1">
          <NavItems />
        </div>
        <div className="mt-auto border-t pt-4">
          <QuotaIndicator />
        </div>
      </aside>

      {/* Main column */}
      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b bg-card/80 px-4 backdrop-blur sm:px-6">
          {/* Mobile nav */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-nav-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 px-4 py-5">
              <Brand />
              <div className="mt-8">
                <NavItems onNavigate={() => setOpen(false)} />
              </div>
              <div className="mt-8 border-t pt-4">
                <QuotaIndicator />
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex-1" />

          <LanguageSwitcher />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2" data-testid="account-menu">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  {initials}
                </span>
                <span className="hidden max-w-[160px] truncate text-sm sm:inline">{user?.email}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/app/account")}>
                <User className="mr-2 h-4 w-4" /> {t("navAccount")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" /> {t("logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
          {children}
        </main>
      </div>
    </div>
  );
};
